import React, { useEffect } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";

import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";

import { useUsersContext } from "../contexts/Users";
import useUserHandlers from "../hooks/useUserHandlers";
import userFormSchema from "../lib/userFormSchema";
import { UserFormData } from "../types/users";

import "../../../../assets/css/wibb/pages/profile.scss";
import Loading from "../../../../components/Loading";
import {
    selectAllSites,
    selectAllUserLevels,
} from "../../../../features/global/globalSlice";
import { selectUser } from "../../../../features/user/userSlice";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import { displayFormAlert } from "../../../../lib/utils";

const UserForm = (): React.ReactNode => {
    const { userId, isAddingUser, setIsAddingUser, fetchStatus } =
        useUsersContext();
    const user = useAppSelector(state =>
        userId ? selectUser(state, userId) : undefined,
    );
    const sites = useAppSelector(selectAllSites);
    const userLevels = useAppSelector(selectAllUserLevels);

    const { handleSubmit, handleError } = useUserHandlers();

    const methods = useForm<UserFormData>({
        mode: "onChange",
        resolver: yupResolver(userFormSchema),
        defaultValues: {
            id: user?.id,
            email: user?.email,
            firstName: user?.meta.firstName,
            lastName: user?.meta.lastName,
            phone: user?.meta.phoneNumbers.primary,
            city: user?.meta.city,
            state: user?.meta.state,
            permissions: sites.reduce(
                (acc, site) => {
                    const userPermission = user?.userLevels.find(
                        l => l.site === site.id,
                    );

                    acc[site.id] = {
                        level: userPermission
                            ? {
                                  value: userPermission?.userLevel,
                                  label: userLevels.find(
                                      ul => ul.id === userPermission?.userLevel,
                                  )?.levelName,
                              }
                            : null,
                    };

                    return acc;
                },
                [] as {
                    level?: {
                        value?: number;
                        label?: string;
                    } | null;
                }[],
            ),
        },
        context: { isAddingUser },
    });

    useEffect(() => {
        methods.reset({
            id: user?.id,
            email: user?.email,
            firstName: user?.meta.firstName,
            lastName: user?.meta.lastName,
            phone: user?.meta.phoneNumbers.primary,
            city: user?.meta.city,
            state: user?.meta.state,
            permissions: sites.reduce(
                (acc, site) => {
                    const userPermission = user?.userLevels.find(
                        l => l.site === site.id,
                    );

                    acc[site.id] = {
                        level: userPermission
                            ? {
                                  value: userPermission?.userLevel,
                                  label: userLevels.find(
                                      ul => ul.id === userPermission?.userLevel,
                                  )?.levelName,
                              }
                            : null,
                    };

                    return acc;
                },
                [] as {
                    level?: {
                        value?: number;
                        label?: string;
                    } | null;
                }[],
            ),
        });
    }, [userId]);

    return userId || isAddingUser ? (
        <Container className="profile">
            <Row>
                <Col>
                    <Form
                        onSubmit={methods.handleSubmit(
                            handleSubmit,
                            handleError,
                        )}
                        id="profileForm"
                    >
                        <Row>
                            <Col>
                                <h2>
                                    {isAddingUser ? "Add New" : "Edit"} User
                                </h2>
                                <p>
                                    <b>Note:</b> All fields are required
                                </p>
                            </Col>
                        </Row>
                        <Row>
                            <Col sm={6}>
                                <Row>
                                    <Col>
                                        <h4>Personal Info</h4>
                                    </Col>
                                </Row>
                                <Form.Group controlId="firstName" as={Row}>
                                    <Form.Label column sm={6}>
                                        First Name
                                    </Form.Label>
                                    <Col sm={6}>
                                        <Form.Control
                                            type="text"
                                            {...methods.register("firstName", {
                                                required:
                                                    "First Name is required",
                                            })}
                                            aria-invalid={
                                                methods.formState.errors
                                                    .firstName
                                                    ? "true"
                                                    : "false"
                                            }
                                        />
                                        {methods.formState.errors.firstName &&
                                            displayFormAlert({
                                                level: "danger",
                                                text: methods.formState.errors
                                                    .firstName
                                                    .message as string,
                                            })}
                                    </Col>
                                </Form.Group>
                                <Form.Group controlId="lastName" as={Row}>
                                    <Form.Label column sm={6}>
                                        Last Name
                                    </Form.Label>
                                    <Col sm={6}>
                                        <Form.Control
                                            type="text"
                                            {...methods.register("lastName", {
                                                required:
                                                    "Last Name is required",
                                            })}
                                            aria-invalid={
                                                methods.formState.errors
                                                    .lastName
                                                    ? "true"
                                                    : "false"
                                            }
                                        />
                                        {methods.formState.errors.lastName &&
                                            displayFormAlert({
                                                level: "danger",
                                                text: methods.formState.errors
                                                    .lastName.message as string,
                                            })}
                                    </Col>
                                </Form.Group>
                            </Col>
                            <Col sm={6}>
                                <Row>
                                    <Col>
                                        <h4>Contact Info</h4>
                                    </Col>
                                </Row>
                                <Form.Group controlId="email" as={Row}>
                                    <Form.Label column sm={6}>
                                        Email
                                    </Form.Label>
                                    <Col sm={6}>
                                        <Form.Control
                                            type="email"
                                            {...methods.register("email", {
                                                required:
                                                    "Email address  is required",
                                            })}
                                            aria-invalid={
                                                methods.formState.errors.email
                                                    ? "true"
                                                    : "false"
                                            }
                                        />
                                        {methods.formState.errors.email &&
                                            displayFormAlert({
                                                level: "danger",
                                                text: methods.formState.errors
                                                    .email.message as string,
                                            })}
                                    </Col>
                                </Form.Group>
                                <Form.Group controlId="phone" as={Row}>
                                    <Form.Label column sm={6}>
                                        Phone
                                    </Form.Label>
                                    <Col sm={6}>
                                        <Form.Control
                                            type="text"
                                            {...methods.register("phone")}
                                            aria-invalid={
                                                methods.formState.errors.phone
                                                    ? "true"
                                                    : "false"
                                            }
                                        />
                                        {methods.formState.errors.phone &&
                                            displayFormAlert({
                                                level: "danger",
                                                text: methods.formState.errors
                                                    .phone.message as string,
                                            })}
                                    </Col>
                                </Form.Group>
                                <Form.Group controlId="city" as={Row}>
                                    <Form.Label column sm={6}>
                                        City
                                    </Form.Label>
                                    <Col sm={6}>
                                        <Form.Control
                                            type="text"
                                            {...methods.register("city")}
                                            aria-invalid={
                                                methods.formState.errors.city
                                                    ? "true"
                                                    : "false"
                                            }
                                        />
                                        {methods.formState.errors.city &&
                                            displayFormAlert({
                                                level: "danger",
                                                text: methods.formState.errors
                                                    .city.message as string,
                                            })}
                                    </Col>
                                </Form.Group>
                                <Form.Group controlId="state" as={Row}>
                                    <Form.Label column sm={6}>
                                        State (abbrev.)
                                    </Form.Label>
                                    <Col sm={6}>
                                        <Form.Control
                                            type="text"
                                            {...methods.register("state")}
                                            aria-invalid={
                                                methods.formState.errors.state
                                                    ? "true"
                                                    : "false"
                                            }
                                        />
                                        {methods.formState.errors.state &&
                                            displayFormAlert({
                                                level: "danger",
                                                text: methods.formState.errors
                                                    .state.message as string,
                                            })}
                                    </Col>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col sm={8}>
                                <Row>
                                    <Col>
                                        <h4>Permissions</h4>
                                    </Col>
                                </Row>
                                {sites.map(s => {
                                    return (
                                        <Form.Group
                                            controlId={`permissions.${s.id}`}
                                            as={Row}
                                            key={s.id}
                                        >
                                            <Form.Label column sm={6}>
                                                {s.displayName}
                                            </Form.Label>
                                            <Col sm={6}>
                                                <Controller
                                                    name={`permissions.${s.id}.level`}
                                                    control={methods.control}
                                                    render={({ field }) => (
                                                        <Select
                                                            {...field}
                                                            aria-labelledby={`permissions.${s.id}.level`}
                                                            isClearable={true}
                                                            isMulti={false}
                                                            isSearchable={false}
                                                            options={[
                                                                ...userLevels,
                                                            ]
                                                                .sort(
                                                                    (a, b) =>
                                                                        a.id -
                                                                        b.id,
                                                                )
                                                                .map(u => {
                                                                    return {
                                                                        value: u.id,
                                                                        label: u.levelName as string,
                                                                    };
                                                                })}
                                                        />
                                                    )}
                                                />
                                            </Col>
                                        </Form.Group>
                                    );
                                })}
                            </Col>
                        </Row>
                        <Row>
                            <Col
                                className={classNames(
                                    "d-flex",
                                    "align-items-baseline",
                                )}
                            >
                                {isAddingUser && (
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        style={{ marginRight: ".75em" }}
                                        onClick={() => setIsAddingUser(false)}
                                    >
                                        Cancel
                                    </Button>
                                )}
                                <Button
                                    variant="success"
                                    type="submit"
                                    disabled={fetchStatus === "pending"}
                                >
                                    {isAddingUser ? "Save" : "Update"}
                                </Button>
                                <Loading
                                    className="mx-3 w-auto"
                                    useContext={useUsersContext}
                                />
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </Container>
    ) : null;
};

export default UserForm;
